import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChevronUp, ChevronDown, GripVertical } from "lucide-react";
import { toast } from "sonner";

export const WIDGETS = [
  { id: "hero", label: "Financial overview (this month)", mods: ["/admin/timesheets"] },
  { id: "reminders", label: "Certificate & sick cert reminders", mods: ["/admin/compliance", "/admin/sick-leave"] },
  { id: "forms", label: "Forms & Checklists", mods: ["/admin/forms"] },
  { id: "whos_out", label: "Who's out today", mods: ["/admin/leave-calendar", "/admin/holidays", "/admin/sick-leave"] },
  { id: "kpis", label: "Key figures", mods: [] },
  { id: "trend", label: "Profit & margin trend", mods: ["/admin/site-profitability"] },
  { id: "financial", label: "Financial tiles", mods: ["/admin/timesheets"] },
  { id: "sites", label: "Site performance lists", mods: ["/admin/site-profitability"] },
  { id: "forecast", label: "Forecast profitability", mods: ["/admin/site-profitability"] },
];

export const availableWidgets = (em) => WIDGETS.filter((w) => !w.mods.length || w.mods.some((m) => em[m] !== false));

export function orderedWidgets(avail, prefs) {
  const order = prefs?.order || [];
  const rank = (id) => { const i = order.indexOf(id); return i === -1 ? 100 + WIDGETS.findIndex((w) => w.id === id) : i; };
  return [...avail].sort((a, b) => rank(a.id) - rank(b.id));
}

export function DashboardCustomise({ open, onOpenChange, avail, prefs, onSaved, isSuper }) {
  const [list, setList] = useState([]);
  const [hidden, setHidden] = useState([]);
  const [drag, setDrag] = useState(null);
  useEffect(() => { if (open) { setList(orderedWidgets(avail, prefs)); setHidden(prefs?.hidden || []); } }, [open, avail, prefs]);
  const move = (from, to) => { if (to < 0 || to >= list.length || from === to) return; const l = [...list]; const [x] = l.splice(from, 1); l.splice(to, 0, x); setList(l); };
  const save = async () => {
    try { const { data } = await api.put("/dashboard/prefs", { order: list.map((w) => w.id), hidden }); toast.success("Dashboard saved"); onSaved(data); onOpenChange(false); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveDefault = async () => {
    try { await api.put("/dashboard/company-default", { order: list.map((w) => w.id), hidden }); toast.success("Saved as this company's default layout"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="dashboard-customise-dialog">
        <DialogHeader><DialogTitle>Customise dashboard</DialogTitle><DialogDescription>Choose what shows and drag to reorder. Sections for switched-off modules aren't listed.</DialogDescription></DialogHeader>
        <div className="space-y-1.5">
          {list.map((w, i) => (
            <div key={w.id} draggable onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => { move(drag, i); setDrag(null); }}
              data-testid={`dash-widget-${w.id}`} className={`flex items-center gap-2 border border-zinc-200 rounded-md px-2 py-2 bg-white ${drag === i ? "opacity-40" : ""}`}>
              <GripVertical className="h-4 w-4 text-zinc-400 cursor-grab" />
              <span className="flex-1 text-sm">{w.label}</span>
              <button type="button" disabled={i === 0} onClick={() => move(i, i - 1)} className="text-zinc-400 disabled:opacity-30" data-testid={`dash-up-${w.id}`}><ChevronUp className="h-4 w-4" /></button>
              <button type="button" disabled={i === list.length - 1} onClick={() => move(i, i + 1)} className="text-zinc-400 disabled:opacity-30" data-testid={`dash-down-${w.id}`}><ChevronDown className="h-4 w-4" /></button>
              <Switch data-testid={`dash-toggle-${w.id}`} checked={!hidden.includes(w.id)} onCheckedChange={(v) => setHidden(v ? hidden.filter((x) => x !== w.id) : [...hidden, w.id])} />
            </div>
          ))}
        </div>
        <div className="flex gap-2 pt-2">
          <Button variant="outline" data-testid="dash-reset" onClick={() => { setList(orderedWidgets(avail, {})); setHidden([]); }}>Reset</Button>
          <Button data-testid="dash-save" onClick={save} className="flex-1 bg-slate-900 text-white">Save dashboard</Button>
        </div>
        {isSuper && <Button data-testid="dash-save-default" variant="outline" onClick={saveDefault} className="w-full border-amber-300 text-amber-800">Save as company default (Amden)</Button>}
      </DialogContent>
    </Dialog>
  );
}
